import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "./api";
import { toast } from "sonner";

const IDLE_MS = 15 * 60 * 1000;   // 15 minutes
const WARN_MS = 14 * 60 * 1000;   // warn 60s before

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("mst_token");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("mst_token");
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (username, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { username, password });
      localStorage.setItem("mst_token", data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const acceptInvite = async (token, username, password, email) => {
    setError("");
    try {
      const { data } = await api.post("/auth/accept-invite", { token, username, password, email: email || null });
      localStorage.setItem("mst_token", data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = () => {
    localStorage.removeItem("mst_token");
    setUser(false);
  };

  // Auto-logout after 15 minutes of inactivity
  useEffect(() => {
    if (!user) return;
    let idleTimer, warnTimer;
    const doLogout = () => {
      localStorage.removeItem("mst_token");
      setUser(false);
      toast.error("Signed out — 15 minutes of inactivity", { duration: 6000 });
    };
    const doWarn = () => {
      toast.warning("Signing out in 60 seconds. Move your mouse or tap to stay in.", { duration: 8000 });
    };
    const reset = () => {
      clearTimeout(idleTimer);
      clearTimeout(warnTimer);
      warnTimer = setTimeout(doWarn, WARN_MS);
      idleTimer = setTimeout(doLogout, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(idleTimer);
      clearTimeout(warnTimer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, error, login, logout, acceptInvite, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}
